import { useCallback, useEffect, useState } from 'react';

import { useDdpEligibleCarriers } from './use-ddp-eligible-carriers';
import { useDdpCountryConfig } from './use-ddp-country-config';
import { hasAcceptedNewTermsAndConditions } from '../components/modals/new-terms-modal/api';
import { useGlobalModalLaunchContext } from '../contexts/modal-context/global-modal-context';
import { useWalletEligibility } from '../wallet/hooks/use-wallet-eligibility';

/**
 * Centralized hook managing the state of DDP Terms & Conditions
 * Supports multi-country DDP (US, CA, etc.) based on seller's home country
 * @returns {{
 *   isAcceptanceRequired: boolean,
 *   isDdpSettingsVisible: boolean,
 *   refreshTermsState: () => Promise<void>,
 * }}
 */
export const useDdpTermsState = () => {
  // Get country-specific DDP configuration and availability
  const { isDdpAvailable, termsLabel } = useDdpCountryConfig();

  const { hasRegisteredWallet } = useWalletEligibility();
  const { hasDdpEligibleCarriers } = useDdpEligibleCarriers();
  const { registerDdpTermsModalClosedCallback } = useGlobalModalLaunchContext();

  const [hasAcceptedDdpTerms, setHasAcceptedDdpTerms] = useState(false);
  const [hasInitiallyChecked, setHasInitiallyChecked] = useState(false);

  const checkTermsAcceptanceAsync = useCallback(async () => {
    if (!termsLabel) {
      setHasInitiallyChecked(true);
      return;
    }

    try {
      const accepted = await hasAcceptedNewTermsAndConditions(termsLabel);
      setHasAcceptedDdpTerms(accepted);
      setHasInitiallyChecked(true);
    } catch {
      setHasAcceptedDdpTerms(false);
      setHasInitiallyChecked(true);
    }
  }, [termsLabel]);

  // True when seller has all prerequisites: country support, registered wallet, and DDP-eligible carrier
  const hasAllPrerequisites = isDdpAvailable && hasRegisteredWallet && hasDdpEligibleCarriers;

  const refreshTermsState = useCallback(async () => {
    if (hasAllPrerequisites) {
      await checkTermsAcceptanceAsync();
    }
  }, [hasAllPrerequisites, checkTermsAcceptanceAsync]);

  useEffect(() => {
    if (hasAllPrerequisites) {
      checkTermsAcceptanceAsync();
    }
  }, [hasAllPrerequisites, checkTermsAcceptanceAsync]);

  useEffect(() => {
    const handleDdpTermsModalClosed = () => {
      refreshTermsState();
    };

    registerDdpTermsModalClosedCallback(handleDdpTermsModalClosed);

    return () => {
      registerDdpTermsModalClosedCallback(null);
    };
  }, [registerDdpTermsModalClosedCallback, refreshTermsState]);

  // Block label creation until terms are confirmed as accepted
  const isAcceptanceRequired = hasAllPrerequisites && (!hasInitiallyChecked || !hasAcceptedDdpTerms);

  // Show DDP settings section only when seller has all prerequisites
  const isDdpSettingsVisible = hasAllPrerequisites;

  return {
    isAcceptanceRequired,
    isDdpSettingsVisible,
    refreshTermsState,
  };
};
